// ── THE COVERAGE STACK, AND WHAT A BAND COSTS TO MOVE ────────────────────────
//
// Every level is priced as the COMPLETE structure a grower would actually buy,
// and the four valid combinations are priced separately rather than bundled:
//
//     RP only  ·  RP + SCO  ·  RP + ECO95  ·  RP + SCO + ECO95
//
// ECO is evaluated on its own because it is not a rider on SCO — with SCO
// reaching 90% for CY2027, ECO is the 90→95 band and stands alone. Bundling the
// two hides that a farm may want one and not the other.
//
// ── THE MARGINAL COST IS THE POINT ───────────────────────────────────────────
//
// Raising the MPCI level does not buy more TOTAL protection. It moves a 5-point
// band off the COUNTY trigger and onto the FARM's own trigger. So the honest
// price of that move is:
//
//     net = (MPCI farmer premium at hi − at lo) + (SCO farmer premium at hi − at lo)
//
// The second term is NEGATIVE: SCO's band shrinks as the base level rises, so
// the grower stops paying for the part they just bought individually. Charging
// only the first term — "85% costs $X more than 80%" — overstates the move by
// exactly the SCO premium displaced, and that is the comparison a grower makes
// unaided.
//
// ⚠ ECO DROPS OUT OF THE MARGINAL, AND THAT IS ARITHMETIC RATHER THAN AN
// ASSUMPTION. ECO's protection is `0.05 × APH × price` — its band is 90→95
// whatever the base level is, and it prices off a single area rate. So the ECO
// premium is identical at 75, 80 and 85 and cancels. It is still PRICED at every
// level (a grower comparing totals needs it) and it is asserted to be constant,
// so if RMA ever makes ECO base-dependent the checker fails rather than the
// marginal quietly going wrong.

// The four stacks. `sco` / `eco` are what the grower elects.
export const STACKS = [
  { key: "RP",         label: "RP only",          sco: false, eco: false },
  { key: "RP_SCO",     label: "RP + SCO",         sco: true,  eco: false },
  { key: "RP_ECO",     label: "RP + ECO95",       sco: false, eco: true  },
  { key: "RP_SCO_ECO", label: "RP + SCO + ECO95", sco: true,  eco: true  },
];

export const CANDIDATE_LEVELS = [0.75, 0.80, 0.85];
export const ANCHOR_LEVEL = 0.80;

const r2 = (n) => (n == null || !isFinite(n) ? null : Math.round(n * 100) / 100);

// ── one level, every stack ───────────────────────────────────────────────────
export function priceLevel({
  state, county, aph, acres, price, vol, practice, unit,
  rateYield = null, adjustedYield = null, priceElectionPct = 1,
  cl, quoteRP, quoteArea, scoTopPct = 90,
}) {
  const m = quoteRP({ state, county, aph, cl, acres, unit, price, vol, plan: "RP",
                      rateYield, adjustedYield, practice, priceElectionPct });
  if (!m) return null;
  // SCO fills the base level up to its top; above that there is no band left.
  const sco = cl < scoTopPct / 100
    ? quoteArea({ state, county, endorsement: "SCO", baseCl: cl, aph, acres, price, vol, practice })
    : null;
  const eco = quoteArea({ state, county, endorsement: "ECO", baseCl: cl, aph, acres, price, vol, practice });

  const stacks = {};
  for (const s of STACKS) {
    if (s.sco && !sco) continue;          // not offered / no band left
    if (s.eco && !eco) continue;
    // ⚠ A PREMIUM THAT COULD NOT BE COMPUTED IS NOT A PREMIUM OF ZERO, and
    // `|| 0` erased the difference right here — before anything downstream
    // could see it. Acres enter RMA rating only through the BU/EU discount
    // band, so the quoter withholds the premium (and only the premium) when
    // no acreage was reported; that withheld value arrived as 0, made the
    // marginal move read "free", and `freeOrBetter` is the one route that
    // BYPASSES the margin test in coverageRecommend. An unpriceable move
    // could therefore be recommended on the grounds that it costs nothing.
    // Live on the Full Strategy Report 2026-09-05: KAST FARMS page 1 printed
    // "COST SCO/ECO $0.00/ac" and "85% would cost another $0.00/ac" on the
    // same page whose reason line said the acreage needed to price it was
    // missing.
    //
    // A STACK THAT DOES NOT INCLUDE AN ENDORSEMENT STILL COSTS 0 FOR IT.
    // That is a real zero and must stay one, or every RP-only stack becomes
    // unpriceable and the whole ladder disappears.
    const mpciNet = m.netAcre != null ? m.netAcre : null;
    const scoNet = !s.sco ? 0 : (sco && sco.netAcre != null ? sco.netAcre : null);
    const ecoNet = !s.eco ? 0 : (eco && eco.netAcre != null ? eco.netAcre : null);
    const netKnown = mpciNet != null && scoNet != null && ecoNet != null;
    const scoCov = s.sco && sco ? sco.covPerAc : 0;
    const ecoCov = s.eco && eco ? eco.covPerAc : 0;
    stacks[s.key] = {
      key: s.key, label: s.label,
      mpciNetAcre: mpciNet != null ? r2(mpciNet) : null,
      scoNetAcre: scoNet != null ? r2(scoNet) : null,
      ecoNetAcre: ecoNet != null ? r2(ecoNet) : null,
      totalNetAcre: netKnown ? r2(mpciNet + scoNet + ecoNet) : null,
      // Stated, so a caller does not have to infer it from a null it might
      // coerce. `Number(null)` is 0 and 0 is finite - the trap that produced
      // this whole defect.
      netPriceable: netKnown,
      individualLiabPerAc: r2(m.liabPerAc),
      areaProtPerAc: r2(scoCov + ecoCov),
      totalProtPerAc: r2((m.liabPerAc || 0) + scoCov + ecoCov),
    };
  }

  return {
    cl,
    mpci: m, sco, eco,
    mpciSubsidyPct: m.subsidy != null ? Math.round(m.subsidy * 100) : null,
    // the bands, stated rather than implied
    individualBand: [0, cl],
    scoBand: sco ? [cl, scoTopPct / 100] : null,
    ecoBand: eco ? [scoTopPct / 100, 0.95] : null,
    stacks,
    scoOffered: !!sco, ecoOffered: !!eco,
  };
}

export function buildLevels(args) {
  const out = [];
  for (const cl of (args.levels || CANDIDATE_LEVELS)) {
    const p = priceLevel({ ...args, cl });
    if (p) out.push(p);
  }
  return out;
}

// ── the marginal move ────────────────────────────────────────────────────────
//
// Net cost of moving the (lo, hi] band from the county trigger to the farm's.
// Computed on the FULL stack the grower would hold in each case, so the SCO
// displacement is included by construction rather than remembered.
export function marginalMove(levels, lo, hi, stackKey = "RP_SCO_ECO") {
  const L = levels.find((x) => Math.abs(x.cl - lo) < 1e-9);
  const H = levels.find((x) => Math.abs(x.cl - hi) < 1e-9);
  if (!L || !H) return null;
  const ls = L.stacks[stackKey], hs = H.stacks[stackKey];
  if (!ls || !hs) return null;

  // ⚠ UNPRICEABLE IS NOT FREE. Every one of these was `|| 0`, so a level the
  // engine could not price differenced to a net of exactly 0 and came back
  // `freeOrBetter: true` - the one flag that bypasses the margin test.
  const priceable = ls.netPriceable !== false && hs.netPriceable !== false
    && ls.mpciNetAcre != null && hs.mpciNetAcre != null
    && ls.scoNetAcre != null && hs.scoNetAcre != null
    && ls.ecoNetAcre != null && hs.ecoNetAcre != null;
  const dMpci = (hs.mpciNetAcre || 0) - (ls.mpciNetAcre || 0);
  const dSco = (hs.scoNetAcre || 0) - (ls.scoNetAcre || 0);   // negative: band shrank
  const dEco = (hs.ecoNetAcre || 0) - (ls.ecoNetAcre || 0);   // ~0 by construction
  const net = dMpci + dSco + dEco;
  const movedLiab = (hs.individualLiabPerAc || 0) - (ls.individualLiabPerAc || 0);

  return {
    from: lo, to: hi, stack: stackKey,
    mpciDelta: r2(dMpci),
    scoDisplaced: r2(dSco),
    ecoDelta: r2(dEco),
    priceable,
    netCostPerAc: priceable ? r2(net) : null,
    // What a grower would compute unaided, kept so the page can show the gap.
    naiveMpciOnlyPerAc: r2(dMpci),
    overstatementPerAc: r2(dMpci - net),
    liabilityMovedPerAc: r2(movedLiab),
    // FAILS CLOSED: no price, no free move.
    freeOrBetter: priceable && net <= 0,
  };
}

// Sanity identity the checker pins: ECO must not depend on the base level.
export function ecoIsLevelInvariant(levels, tol = 0.011) {
  const vals = levels
    .map((l) => (l.stacks.RP_SCO_ECO || l.stacks.RP_ECO || {}).ecoNetAcre)
    .filter((v) => v != null);
  if (vals.length < 2) return null;
  return (Math.max(...vals) - Math.min(...vals)) <= tol;
}
